/**
 * Aufgabenauswahl im ZEO-Dialog (02.11.2026): Suche (Titel, Beschreibung, Klient, Institution, Aktenzeichen, Vorgang) serverseitig,
 * Seiten statt vollständiger Liste, Filter Status/Herkunft, Umschalter "Nur dieser Klient" / "Alle Betreuten".
 * Unteraufgaben sind eingerückt markiert. Wählbar sind nur Aufgaben des Chat-Klienten (auswaehlbar()).
 */
import { useEffect, useState } from 'react';
import { betreuungAPI } from '@/api/betreuungAPI';
import { suchParameter, auswaehlbar, seiten, faelligkeit, STATUS_FILTER, STATUS_LABEL } from './zeoAufgabenSuche';

const datumDe = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : null);

export default function ZeoAufgabenSuche({ klientId, taskId, onWahl }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('offen');
  const [herkunft, setHerkunft] = useState('alle');
  const [bereich, setBereich] = useState('klient');
  const [seite, setSeite] = useState(1);
  const [erg, setErg] = useState({ items: [], gesamt: 0, facetten: {} });
  const [laedt, setLaedt] = useState(false);

  useEffect(() => { setSeite(1); }, [q, filter, herkunft, bereich, klientId]);
  useEffect(() => {
    if (!klientId) return undefined;
    let aktiv = true;                       // spaete Antworten (alter Klient/Suchbegriff) verwerfen
    setLaedt(true);
    const t = setTimeout(() => {
      betreuungAPI.sucheAufgaben(suchParameter({ q, filter, herkunft, bereich, klientId, seite }))
        .then((r) => { if (aktiv) setErg({ items: r.items || [], gesamt: r.gesamt || 0, facetten: r.facetten || {} }); })
        .catch(() => { if (aktiv) setErg({ items: [], gesamt: 0, facetten: {} }); })
        .finally(() => { if (aktiv) setLaedt(false); });
    }, 250);
    return () => { aktiv = false; clearTimeout(t); };
  }, [q, filter, herkunft, bereich, klientId, seite]);

  const n = seiten(erg.gesamt);
  return (
    <div data-testid="zeo-aufgaben-suche-dialog">
      <div className="flex gap-1 mb-2" role="tablist">
        {[['klient', 'Nur dieser Klient'], ['alle', 'Alle Betreuten']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setBereich(k)} aria-pressed={bereich === k} data-testid={`zeo-aufgaben-bereich-${k}`}
            className={`text-xs px-2 py-1 rounded-md border ${bereich === k ? 'bg-violet-600 text-white border-violet-600' : 'border-slate-200 text-slate-600'}`}>{l}</button>
        ))}
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Aufgabe suchen (Titel, Klient, Institution, Aktenzeichen, Vorgang) …"
        data-testid="zeo-aufgaben-suchfeld" className="w-full text-sm border border-violet-100 rounded-lg px-3 py-2 mb-2" />
      <div className="flex flex-wrap items-center gap-1 mb-2">
        {STATUS_FILTER.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} aria-pressed={filter === k} data-testid={`zeo-aufgaben-status-${k}`}
            className={`text-xs px-2 py-0.5 rounded-full border ${filter === k ? 'bg-violet-100 border-violet-400 text-violet-800' : 'border-slate-200 text-slate-600'}`}>{l}</button>
        ))}
        <select value={herkunft} onChange={(e) => setHerkunft(e.target.value)} data-testid="zeo-aufgaben-herkunft"
          className="text-xs border border-slate-200 rounded-md px-1 py-0.5 ml-auto">
          <option value="alle">Jede Herkunft</option>
          {(erg.facetten.herkunft || []).map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>
      <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="zeo-aufgaben-treffer">
        <button type="button" onClick={() => onWahl('')} data-testid="zeo-aufgabe-keine"
          className={`w-full text-left text-sm px-3 py-1.5 rounded-lg border ${!taskId ? 'border-violet-400 bg-violet-50' : 'border-transparent hover:bg-slate-50'}`}>keine Aufgabe</button>
        {erg.items.map((a) => {
          const ok = auswaehlbar(a, klientId);
          const unter = Boolean(a.eltern_aufgabe_id);
          const f = faelligkeit(a);
          return (
            <button key={a.id} type="button" disabled={!ok} onClick={() => ok && onWahl(a.id)} data-testid={`zeo-aufgabe-treffer-${a.id}`}
              data-unteraufgabe={unter ? 'ja' : 'nein'} data-waehlbar={ok ? 'ja' : 'nein'}
              title={ok ? undefined : 'Gehört zu einem anderen Klienten – dafür einen neuen Chat mit diesem Klienten starten'}
              className={`w-full text-left text-sm px-3 py-1.5 rounded-lg border ${unter ? 'ml-4 w-[calc(100%-1rem)] border-l-4 border-l-violet-200' : ''} ${taskId === a.id ? 'border-violet-400 bg-violet-50' : 'border-transparent hover:bg-slate-50'} ${ok ? '' : 'opacity-50 cursor-not-allowed'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{unter && <span className="text-[10px] text-violet-600 mr-1">Unteraufgabe</span>}{a.titel || 'Ohne Titel'}</span>
                <span className="text-[11px] text-slate-500 shrink-0">{STATUS_LABEL[a.status] || a.status}</span>
              </div>
              <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3">
                <span>{a.klient_anzeige || 'ohne Klient'}</span>
                <span>{f ? `Frist ${datumDe(f)}` : 'ohne Frist'}</span>
                {a.vorgang_anzeige && <span>Vorgang: {a.vorgang_anzeige}</span>}
                {!ok && <span className="text-amber-700">anderer Klient – nicht übernehmbar</span>}
              </div>
            </button>
          );
        })}
        {!laedt && !erg.items.length && <p className="text-xs text-slate-400 px-3 py-2" data-testid="zeo-aufgaben-leer">Keine passenden Aufgaben</p>}
      </div>
      <div className="flex items-center justify-between mt-2 text-xs text-slate-500" data-testid="zeo-aufgaben-seiten">
        <span>{erg.gesamt} Treffer · Seite {seite} von {n}</span>
        <span className="flex gap-1">
          <button type="button" disabled={seite <= 1} onClick={() => setSeite(seite - 1)} data-testid="zeo-aufgaben-zurueck" className="px-2 py-0.5 border rounded disabled:opacity-40">‹</button>
          <button type="button" disabled={seite >= n} onClick={() => setSeite(seite + 1)} data-testid="zeo-aufgaben-weiter" className="px-2 py-0.5 border rounded disabled:opacity-40">›</button>
        </span>
      </div>
    </div>
  );
}
